import { Button } from '@/components/atoms';
import { BackArrowIcon } from '@/components/atoms/BackArrowIcon';
import { Typography } from '@/components/atoms/Typography';
import { useUIColors, useWebViewport } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { BREAKPOINTS } from '@/core/utils/responsive';
import React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface PaymentFlowMobileScreenProps {
  visible: boolean;
  title: string;
  onBack: () => void;
  backAccessibilityLabel: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}

export interface PaymentFlowMobilePairFooterProps {
  secondaryLabel: string;
  primaryLabel: string;
  onSecondary: () => void;
  onPrimary: () => void;
  primaryDisabled?: boolean;
}

const webFontSmooth =
  Platform.OS === 'web'
    ? ({
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      } as object)
    : undefined;

/** Phone-only full-screen overlay. Medium + large keep the Figma dialog frame. */
export function useIsPaymentPhoneLayout(): boolean {
  const { breakpointWidth } = useWebViewport();
  return breakpointWidth < BREAKPOINTS.PHONE;
}

/**
 * Cancel + Continue / Cancel + confirm — equal-width sticky footer.
 */
export function PaymentFlowMobilePairFooter({
  secondaryLabel,
  primaryLabel,
  onSecondary,
  onPrimary,
  primaryDisabled = false,
}: Readonly<PaymentFlowMobilePairFooterProps>) {
  return (
    <View style={styles.pairRow}>
      <Button
        title={secondaryLabel}
        variant="text"
        onPress={onSecondary}
        style={styles.pairCancelBtn}
        textStyle={styles.pairCancelLabel}
      />
      <Button
        title={primaryLabel}
        variant="primary"
        fullWidth
        disabled={primaryDisabled}
        onPress={onPrimary}
        style={styles.pairBtn}
        textStyle={styles.pairLabel}
      />
    </View>
  );
}

/**
 * Full-viewport Pay Now chrome on small web (header + scroll + sticky footer).
 * Overlay only — not a new Expo route.
 */
export function PaymentFlowMobileScreen({
  visible,
  title,
  onBack,
  backAccessibilityLabel,
  children,
  footer,
}: Readonly<PaymentFlowMobileScreenProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const insets = useSafeAreaInsets();

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onBack}
    >
      <View
        style={[
          styles.root,
          {
            backgroundColor: colors.backgroundLevel2,
            paddingTop: insets.top,
            paddingBottom: Math.max(insets.bottom, 16),
          },
        ]}
      >
        <View style={styles.header}>
          <Pressable
            onPress={onBack}
            style={styles.backBtn}
            accessibilityRole="button"
            accessibilityLabel={backAccessibilityLabel}
          >
            <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
              <BackArrowIcon size={24} color={colors.fontPrimary} />
            </View>
          </Pressable>
          <Typography
            variant="title3"
            color="primary"
            style={styles.headerTitle}
          >
            {title}
          </Typography>
          <View style={styles.headerSpacer} />
        </View>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          bounces={false}
        >
          {children}
        </ScrollView>
        <View style={styles.footer}>{footer}</View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    minHeight: 48,
  },
  backBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 20,
    lineHeight: 20,
    ...webFontSmooth,
  },
  headerSpacer: {
    width: 44,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
    gap: 16,
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  pairRow: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  pairBtn: {
    flex: 1,
    height: 44,
    borderRadius: 4,
  },
  /** Label sits on the footer's leading padding edge, not centred in its half. */
  pairCancelBtn: {
    flex: 1,
    height: 44,
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  pairLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 14,
    lineHeight: 14,
    textAlign: 'center',
    ...webFontSmooth,
  },
  pairCancelLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    paddingBottom: 2,
    ...(Platform.OS === 'web'
      ? ({ textUnderlineOffset: 3, ...webFontSmooth } as object)
      : webFontSmooth),
  },
});

export default PaymentFlowMobileScreen;
